/** Insights library page — /insights */

import { pageRoute, serviceRoute } from "./paths.js";
import { serviceNav } from "./content.js";
import { getAllInsightDetails } from "./insightDetails.js";

export const INSIGHTS_PAGE_SIZE = 6;

export const insightsPage = {
  documentTitle: "Taldel — Insights",
  hero: {
    badge: "Insights",
    titleSegments: [
      { text: "Field notes from ", highlight: null },
      { text: "delivery", highlight: "primary" },
      { text: ", not ", highlight: null },
      { text: "decks", highlight: "secondary" },
      { text: ".", highlight: null },
    ],
    subtitle:
      "Short reads across web, AI, apps, design, cyber security, and staffing. Filter by service, or browse everything.",
    ctaPrimary: "Talk to us",
    ctaPrimaryHref: pageRoute("insights", "contact"),
    ctaSecondary: "Explore services",
    ctaSecondaryHref: serviceRoute("web-development"),
    panel: {
      kicker: "Library",
      title: "Six lanes. One voice.",
      body: "Pick a service tag to see that lane’s notes — or leave All selected to read across the whole practice.",
      stats: [
        { value: String(getAllInsightDetails().length), label: "Notes" },
        { value: String(serviceNav.length), label: "Service tags" },
        { value: `${INSIGHTS_PAGE_SIZE}`, label: "Per page" },
      ],
    },
  },
  feed: {
    badge: "Library",
    titleSegments: [
      { text: "Browse by ", highlight: null },
      { text: "service", highlight: "primary" },
    ],
    subtitle: "Select a tag to filter. Select All — or the active tag again — to show every insight.",
  },
};

export function getInsightServiceTags() {
  return [{ slug: "", label: "All" }, ...serviceNav.map((item) => ({ slug: item.slug, label: item.label }))];
}

export function parseInsightsQuery(search = "") {
  const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
  const rawService = String(params.get("service") || "").toLowerCase();
  const service = serviceNav.some((item) => item.slug === rawService) ? rawService : "";
  const parsedPage = Number.parseInt(String(params.get("page") || "1"), 10);
  const page = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;
  return { service, page };
}

export function insightsListHref({ service = "", page = 1 } = {}) {
  const path = pageRoute("insights");
  const params = new URLSearchParams();
  if (service) params.set("service", service);
  if (page > 1) params.set("page", String(page));
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

/** Round-robin cards across services so a grid row mixes lanes (Web, Cyber, …). */
function interleaveByService(items) {
  const buckets = new Map();
  for (const item of items) {
    const key = item.serviceId || "";
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(item);
  }

  const seen = new Set();
  const order = [];
  for (const item of serviceNav) {
    if (buckets.has(item.slug)) {
      order.push(item.slug);
      seen.add(item.slug);
    }
  }
  for (const key of buckets.keys()) {
    if (!seen.has(key)) order.push(key);
  }

  const queues = order.map((key) => buckets.get(key));
  const mixed = [];
  let added = true;
  while (added) {
    added = false;
    for (const queue of queues) {
      if (queue.length) {
        mixed.push(queue.shift());
        added = true;
      }
    }
  }
  return mixed;
}

export function getFilteredInsights(service = "") {
  const all = getAllInsightDetails();
  if (!service) return interleaveByService(all);
  return all.filter((item) => item.serviceId === service);
}

export function paginateInsights(items, page = 1, size = INSIGHTS_PAGE_SIZE) {
  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / size) || 1);
  const safePage = Math.min(Math.max(1, page), pageCount);
  const start = (safePage - 1) * size;
  return {
    items: items.slice(start, start + size),
    page: safePage,
    pageCount,
    total,
  };
}

export function insightsRoute(section = "") {
  return pageRoute("insights", section);
}
